import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// import TopInfo from "./TopInfo";
import ScrollToTop from "./ScrollToTop";
import HeaderDropdownData from "./data/HeaderDropdownData";
import LoadingBar from "./ProgressBarComponent";

import logo from "./images/header/pthinks-logo.webp";
import menuIcon from "./images/header/menu.webp";
import user from "./images/header/user.webp";

import "./styles/Header.css";

function Header() {
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0); // Progress state
    const [isLoading, setIsLoading] = useState(false); // Loading visibility state

    const { t, i18n } = useTranslation();

    const location = useLocation();
    const navigate = useNavigate();

    const [activeDropdown, setActiveDropdown] = useState(null); // State for mobile dropdowns

    // Function to toggle the mobile menu
    const toggleMobileMenu = () => {
        setMobileMenuVisible(!mobileMenuVisible);
        setActiveDropdown(null); // Reset dropdowns when closing menu
    };

    const handleDropdownClick = (name) => {
        setActiveDropdown(activeDropdown === name ? null : name);
    };

    // Scroll to a specific section of the page
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Handle the navigation to the "Customers" section
    const handleCustomersClick = () => {
        if (location.pathname === "/") {
            scrollToSection("landing_customers");
        } else {
            simulateLoading(() => navigate("/#landing_customers"));
        }
    };

    // Handle navigation for other routes
    const handleClick = (path) => {
        simulateLoading(() => {
            ScrollToTop();
            navigate(path);
        });
    };

    // Simulate the loading bar functionality
    // Simulate the loading bar functionality
    // Simulate the loading bar functionality
    const simulateLoading = (callback) => {
        // Reset progress and show the loading bar
        setIsLoading(true);
        setLoadingProgress(0);

        const interval = setInterval(() => {
            setLoadingProgress((prevProgress) => {
                if (prevProgress >= 100) {
                    clearInterval(interval);
                    setIsLoading(false); // Hide the loading bar
                    if (callback) callback(); // Trigger callback for navigation or other actions
                    return 100;
                }
                return prevProgress + 10; // Increment progress smoothly
            });
        }, 100); // Update progress every 100ms

        // Ensure the loading bar hides after 3 seconds (3000ms)
        setTimeout(() => {
            setIsLoading(false);
        }, 3000);
    };

    return (
        <>
            {/* Conditionally render the LoadingBar */}
            {isLoading && <LoadingBar progress={loadingProgress} />}

            <header className="header">
                {/*<TopInfo />*/}

                <div className="header_main">
                    {/* Logo */}
                    <Link
                        onClick={() => handleClick("/")}
                        className="header_logo_div"
                        to="/"
                    >
                        <img
                            src={logo}
                            className="header_logo"
                            alt="PthinkS Logo"
                        />
                    </Link>

                    {/* Navigation Items */}
                    <div className="header_components">
                        <div className="header_content_item">
                            {t('header.services')}{" "}
                            <span className="header_content_item_arrow">
                                &#x2B9F;
                            </span>
                            <div className="header_dropdown">
                                {HeaderDropdownData.Services.map(
                                    (item, index) => (
                                        <Link
                                            key={index}
                                            className="header_dropdown_content_item"
                                            to={item.path}
                                            onClick={() => handleClick(item.path)}
                                        >
                                            {item.title}
                                        </Link>
                                    )
                                )}
                            </div>
                        </div>
                        <div className="header_content_item">
                            {t('header.training')}{" "}
                            <span className="header_content_item_arrow">
                                &#x2B9F;
                            </span>
                            <div className="header_dropdown">
                                {HeaderDropdownData.Training.map(
                                    (item, index) => (
                                        <a
                                            key={index}
                                            className="header_dropdown_content_item"
                                        >
                                            {item}
                                        </a>
                                    )
                                )}
                            </div>
                        </div>
                        <div
                            className="header_content_item"
                            onClick={handleCustomersClick}
                        >
                            {t('header.customers')}
                        </div>
                        <Link
                            onClick={() => handleClick("/about")}
                            className="header_content_item"
                            to="/about"
                        >
                            {t('header.about')}
                        </Link>
                        <Link
                            onClick={() => handleClick("/team")}
                            className="header_content_item"
                            to="/team"
                        >
                            {t('header.team')}
                        </Link>
                        <Link
                            onClick={() => handleClick("/reach-us")}
                            className="header_content_item"
                            to="/reach-us"
                        >
                            {t('header.reach_us')}
                        </Link>
                        <Link
                            onClick={() => handleClick("/careers")}
                            className="header_content_item"
                            to="/careers"
                        >
                            {t('header.careers')}
                        </Link>
                    </div>

                    {/* Actions (Language + Login) */}
                    <div className="header_actions">
                        <div className="language_switcher" onMouseEnter={() => setActiveDropdown('Language')} onMouseLeave={() => setActiveDropdown(null)}>
                            <div className="language_switcher_current">
                                <span className="language_icon">🌐</span> {i18n.language === 'de' ? 'DE' : 'EN'}
                                <span className="language_arrow">&#x2B9F;</span>
                            </div>
                            <div className={`language_dropdown ${activeDropdown === 'Language' ? 'active' : ''}`}>
                                <div 
                                    className={`language_option ${i18n.language === 'en' ? 'selected' : ''}`}
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        i18n.changeLanguage('en').then(() => window.location.reload()); 
                                        setActiveDropdown(null); 
                                    }}
                                >
                                    {t('header.english')}
                                </div>
                                <div 
                                    className={`language_option ${i18n.language === 'de' ? 'selected' : ''}`}
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        i18n.changeLanguage('de').then(() => window.location.reload()); 
                                        setActiveDropdown(null); 
                                    }}
                                >
                                    {t('header.german')}
                                </div>
                            </div>
                        </div>

                        {/* Login Button */}
                        <a
                            href="http://pthinks.com:2095/"
                            target="_blank"
                            rel="noreferrer"
                            className="header_content_item_btn"
                        >
                            <img
                                className="header_content_item_btn_img"
                                src={user}
                                alt="User Login"
                            />
                            {t('header.login')}
                        </a>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <div className="header_toggle" onClick={toggleMobileMenu}>
                        <img
                            className="header_toggle_icon"
                            src={menuIcon}
                            alt="Menu Icon"
                        />
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuVisible && (
                    <div className="header_toggle_menu">
                        <div
                            className="header_content_item"
                            onClick={() => handleDropdownClick('Services')}
                        >
                            {t('header.services')}
                            <div className={`header_dropdown ${activeDropdown === 'Services' ? 'active' : ''}`}>
                                {HeaderDropdownData.Services.map(
                                    (item, index) => (
                                        <Link
                                            key={index}
                                            className="header_dropdown_content_item"
                                            to={item.path}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleMobileMenu();
                                                handleClick(item.path);
                                            }}
                                        >
                                            {item.title}
                                        </Link>
                                    )
                                )}
                            </div>
                        </div>
                        <div
                            className="header_content_item"
                            onClick={() => handleDropdownClick('Training')}
                        >
                            {t('header.training')}
                            <div className={`header_dropdown ${activeDropdown === 'Training' ? 'active' : ''}`}>
                                {HeaderDropdownData.Training.map(
                                    (item, index) => (
                                        <a
                                            key={index}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleMobileMenu();
                                            }}
                                            className="header_dropdown_content_item"
                                        >
                                            {item}
                                        </a>
                                    )
                                )}
                            </div>
                        </div>
                        <Link
                            onClick={() => handleClick("/")}
                            className="header_content_item"
                        >
                            {t('header.customers')}
                        </Link>
                        <Link
                            onClick={() => handleClick("/about")}
                            className="header_content_item"
                        >
                            {t('header.about')}
                        </Link>
                        <Link
                            onClick={() => handleClick("/team")}
                            className="header_content_item"
                        >
                            {t('header.team')}
                        </Link>
                        <Link
                            onClick={() => handleClick("/reach-us")}
                            className="header_content_item"
                        >
                            {t('header.reach_us')}
                        </Link>
                        <Link
                            onClick={() => handleClick("/careers")}
                            className="header_content_item"
                        >
                            {t('header.careers')}
                        </Link>
                        <div className="language_switcher_mobile" onClick={() => handleDropdownClick('LanguageMobile')}>
                            <div className="language_switcher_current">
                                <span className="language_icon">🌐</span> {i18n.language === 'de' ? 'DE' : 'EN'}
                                <span className="language_arrow">&#x2B9F;</span>
                            </div>
                            <div className={`language_dropdown ${activeDropdown === 'LanguageMobile' ? 'active' : ''}`}>
                                <div 
                                    className={`language_option ${i18n.language === 'en' ? 'selected' : ''}`}
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        i18n.changeLanguage('en').then(() => window.location.reload()); 
                                        setMobileMenuVisible(false); 
                                    }}
                                >
                                    {t('header.english')}
                                </div>
                                <div 
                                    className={`language_option ${i18n.language === 'de' ? 'selected' : ''}`}
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        i18n.changeLanguage('de').then(() => window.location.reload()); 
                                        setMobileMenuVisible(false); 
                                    }}
                                >
                                    {t('header.german')}
                                </div>
                            </div>
                        </div>
                        <a
                            href="http://pthinks.com:2095/"
                            target="_blank"
                            rel="noreferrer"
                            className="header_content_item_btn_sub"
                        >
                            <img
                                className="header_content_item_btn_img"
                                src={user}
                                alt="User Login"
                            />
                            {t('header.login')}
                        </a>
                    </div>
                )}
            </header>
        </>
    );
}

export default Header;